import React from 'react';
import { Marker, Popup, Tooltip } from 'react-leaflet';
import theme from '../../../theme';
import { makeRouteStopIcon, makeDriverIcon } from './mapIcons';
import { segmentBearing } from './mapGeometry';
import { byVisitOrder } from './stopOrdering';
import { MIN_LINE_POINTS, hasCoords, buildPathPositions } from './routePaths';
import {
  LINE,
  line,
  hoverCardDismisser,
  buildTrailLayers,
} from './trailLayers';
import {
  byStopIndex,
  stripStopSuffix,
  isSeverelyLate,
  stopDisplayState,
} from '../../../utils/composeDriverRecords';
import StopPopupContent from './StopPopupContent';
import DriverPopupContent from './DriverPopupContent';
//
// Layers for ONE route in the Single-route view. The color language is
// strict: solid route blue = where the truck actually drove, dashed ink = the
// predicted path ahead; stop state is the disc (green stocked, filled ink in
// flight, hollow ink still owed) and only the severely-late driver marker
// turns red.
//
// The driven path is the driver's RECORDED GPS trail (one line per driver who
// contributed positions, broken wherever the phone stopped reporting). With
// no recorded trail there is deliberately NO solid line at all — the pane
// badges the route "No GPS reported" instead of faking a driven path from
// stop-to-stop chords. Dots mark the individual readings, faint dotted
// connectors and diamonds mark the GPS gaps between them, and violet dotted
// connectors link in the stops a second driver stocked.
//
// Stop discs are numbered by planned stop index unless the pane hands in a
// `stopNumbers` map (the Stops panel's sort mode: planned, or 1..n by actual
// visit order) — and a stocked stop is ALWAYS green, in or out of planned
// order. The order story lives in the Stops panel, not in disc colors.
//
// Plain function (not a hook) so it stays unit-testable without a harness.

const OTHER_DRIVER_DASH = '2 7';

function stopMarker(driver, stop, iconOptions) {
  return (
    <Marker
      key={`stop-${driver.routeId}-${stop.stopIndex}-${stop.stopId}`}
      position={[stop.latitude, stop.longitude]}
      icon={makeRouteStopIcon(iconOptions)}
    >
      <Popup>
        <StopPopupContent stop={stop} driver={driver} />
      </Popup>
    </Marker>
  );
}

// Routes are sometimes shared: a second driver stocks a stop or two. Their
// work isn't on the primary driver's GPS trail, so these violet dotted
// connectors link it into the route's story — and say who took the stop.
// Needs the route's own driver email to compare against; without one nobody
// is "another".
function otherDriverConnectors(driver, doneStops, onTrailHover) {
  const primaryEmail = (driver.driverEmail || '').toLowerCase();
  if (!primaryEmail) {
    return [];
  }
  const clearHoverCard = hoverCardDismisser(onTrailHover);
  const connectors = [];
  for (let index = 1; index < doneStops.length; index += 1) {
    const doneStop = doneStops[index];
    const stockedBy = doneStop.stockedBy || '';
    if (stockedBy && stockedBy.toLowerCase() !== primaryEmail) {
      connectors.push(
        line(
          `pl-other-${driver.routeId}-${index}`,
          buildPathPositions([doneStops[index - 1], doneStop]),
          LINE.dashed,
          {
            color: theme.colors.statusOutOfSequence,
            dashArray: OTHER_DRIVER_DASH,
            tooltip: (
              <Tooltip sticky>{`Stocked by ${
                doneStop.stockedByName || stockedBy
              }`}</Tooltip>
            ),
            onMouseOver: clearHoverCard,
          },
        ),
      );
    }
  }
  return connectors;
}

// The three groups the builder reasons about: stocked stops in the order they
// were actually visited, the one stop in flight (or null), and the stops still
// owed in planned order.
function partitionStopsByState(stops) {
  return {
    doneStops: stops.filter((stop) => stop.state === 'done').sort(byVisitOrder),
    currentStop: stops.find((stop) => stop.state === 'current') || null,
    pendingStops: stops
      .filter((stop) => stop.state === 'pending')
      .sort(byStopIndex),
  };
}

function routeLayers(
  driver,
  stops,
  { stopNumbers, onTrailHover, stopStateFilter },
) {
  const polylines = [];
  const { doneStops, currentStop, pendingStops } = partitionStopsByState(stops);
  const lastDone = doneStops[doneStops.length - 1] || null;
  // Where the driver is headed: the stop they scanned into, else the one
  // their newest drive leg points at (still pending until they scan in).
  const headingStop =
    currentStop ||
    pendingStops.find((stop) => stop.stopId === driver.nextStopId) ||
    null;

  // Recorded driven path: where the driver actually went. This is the ONLY
  // solid line the map draws — no GPS means no driven line. Chaining the
  // stocked stops with chords instead would read as a road the truck took,
  // and it never took it; the green discs already tell the completion story.
  // Recorded POINTS, not drawn lines, decide `hasTrail`: a trail reported
  // sparsely enough that every run stands alone still says where the driver
  // was, and the gap connectors carry it between them.
  const pingTrail = driver.pingTrail || [];
  const { trailPolylines, gapPolylines, gapMarkers, trailDots, startMarker } =
    buildTrailLayers(driver, pingTrail, { onTrailHover });
  const hasTrail = pingTrail.length >= MIN_LINE_POINTS;
  polylines.push(...trailPolylines, ...gapPolylines);

  polylines.push(...otherDriverConnectors(driver, doneStops, onTrailHover));

  // In-flight hop toward the heading stop: a straight chord from the
  // driver's latest recorded position when a trail is drawn — that IS where
  // they are right now. With no trail it starts at that stop's departure
  // point instead (its leg origin, falling back to the last done stop).
  const lastTrailPoint = hasTrail ? pingTrail[pingTrail.length - 1] : null;
  let inFlightFrom = null;
  if (headingStop && lastTrailPoint) {
    inFlightFrom = {
      latitude: lastTrailPoint.latitude,
      longitude: lastTrailPoint.longitude,
    };
  } else if (headingStop) {
    inFlightFrom =
      (headingStop.legOrigin &&
        doneStops.find(
          (stop) =>
            stripStopSuffix(stop.stopId) ===
            stripStopSuffix(headingStop.legOrigin),
        )) ||
      lastDone;
  }
  if (inFlightFrom && headingStop) {
    polylines.push(
      line(
        `pl-flt-${driver.routeId}`,
        buildPathPositions([inFlightFrom, headingStop]),
        LINE.dashed,
        // Predictions draw in ink, not route blue: blue is reserved for where
        // the truck has actually been.
        { color: theme.colors.markerInk },
      ),
    );
  }

  // Predicted chain through the remaining pending stops, in planned order.
  const chainStart = currentStop || lastDone;
  const chainStops = chainStart ? [chainStart, ...pendingStops] : pendingStops;
  const chainBackward = Boolean(
    currentStop &&
      pendingStops.length > 0 &&
      currentStop.stopIndex > pendingStops[0].stopIndex,
  );
  if (chainStops.length >= MIN_LINE_POINTS) {
    polylines.push(
      line(
        `pl-rem-${driver.routeId}`,
        buildPathPositions(chainStops),
        chainBackward ? LINE.faint : LINE.dashed,
        { color: theme.colors.markerInk },
      ),
    );
  }

  // Disc numbers follow the pane's sort mode when a numbering map is given
  // (planned index, or 1..n by actual visit order); planned index otherwise.
  // The disc itself is the stop's display state (STOP_DISC_FILL): a stocked
  // stop is green whatever order it was reached in, a passed-over pending
  // stop reads skipped.
  // The legend's stop-state filter narrows the MARKERS only — lines still
  // tell the whole route's story so a filtered map keeps its shape.
  const markerStops =
    stopStateFilter && stopStateFilter.size > 0
      ? stops.filter((stop) => stopStateFilter.has(stopDisplayState(stop)))
      : stops;
  const stopMarkers = [...markerStops].sort(byStopIndex).map((stop) =>
    stopMarker(driver, stop, {
      label: stopNumbers ? stopNumbers.get(stop.stopId) : stop.stopIndex,
      fill: stopDisplayState(stop),
      pulse: stop.state === 'current',
    }),
  );

  return {
    polylines,
    stopMarkers,
    headingStop,
    startMarker,
    trailDots,
    gapMarkers,
  };
}

// Chevron toward the stop the driver is headed to when both have positions;
// a dot otherwise. Colored by status tier, except `severe` (more than
// SEVERELY_LATE_MIN behind), which turns it red.
function driverMarker(driver, headingStop) {
  if (!hasCoords(driver)) {
    return null;
  }
  const here = [driver.latitude, driver.longitude];
  const bearing =
    headingStop && hasCoords(headingStop)
      ? segmentBearing(here, [headingStop.latitude, headingStop.longitude])
      : null;
  return (
    <Marker
      key={`driver-${driver.routeId}`}
      position={here}
      icon={makeDriverIcon(driver, {
        severe: isSeverelyLate(driver.etaDeltaMin),
        bearing,
      })}
      zIndexOffset={1000}
    >
      <Popup>
        <DriverPopupContent driver={driver} />
      </Popup>
    </Marker>
  );
}

// `stopNumbers` (optional Map stopId → number, from stopOrdering) overrides
// the planned index shown on each disc; `onTrailHover` receives the recorded
// point under the cursor, or null once it leaves; `stopStateFilter` (a Set of
// display states, see stopDisplayState) keeps only those stops' markers —
// empty or null shows every stop.
export default function buildSingleRouteLayers(
  driver,
  { stopNumbers = null, onTrailHover = null, stopStateFilter = null } = {},
) {
  const stops = (driver.routeStops || []).filter(hasCoords);
  const built = routeLayers(driver, stops, {
    stopNumbers,
    onTrailHover,
    stopStateFilter,
  });
  return {
    polylines: built.polylines,
    stopMarkers: built.stopMarkers,
    startMarker: built.startMarker,
    trailDots: built.trailDots,
    gapMarkers: built.gapMarkers,
    driverMarker: driverMarker(driver, built.headingStop),
  };
}
